import { useCapability } from '../../../../hooks/use-capability';
import { useLivingSummary } from '../../hooks/use-living-summary';

import type { ConversationDetail } from '../../../../lib/types';

/**
 * LivingSummary — bloco canonical Brief 2A r7b (Task 52 v2 — fiel ao print
 * design-references/brief-2a/r7b/standalone.html).
 *
 * Estrutura canonical:
 *   - Container `.living-summary` com background `var(--ai-bg)` + max-height
 *     38% do painel direito + overflow-y interno + scrollbar fino.
 *   - Header `.living-summary__head` com ícone sparkles + "Resumo vivo"
 *     + tempo relativo `atualizado há Xs` (mono, direita).
 *   - 5 blocos canonical mapeados a partir do schema backend v1
 *     (summary-agent gera 6 blocos; design tem 5 distintos — mapping
 *     best-effort; backend prompt v2 alinhamento canonical fica para
 *     Wave 2 — Task 56).
 *
 *     SITUAÇÃO          ← blocks.contextOriginal
 *     CLIENTE QUER      ← blocks.necessidadeAtual
 *     ATENDENTE FEZ     ← blocks.evolucaoConversa
 *     PENDENTE DO CLIENTE ← blocks.pendenciasAcordos (array)
 *     PRÓXIMOS PASSOS   ← (placeholder v1; backend prompt v2 adicionará campo)
 *
 *   - `blocks.confiancaSentimento` e `blocks.riscosObservacoes` NÃO
 *     renderizam (não-canonical no design — sentiment já aparece como
 *     SensitivityBadge no header da conversa).
 */
export function LivingSummary({ conversation }: { conversation: ConversationDetail }): JSX.Element {
  const summary = useLivingSummary(conversation.id);
  // Gate do botão: quem pode LER o resumo (cap `ai-summary-basic`, que já
  // habilita este painel) pode pedir refresh. Gateia o render do botão — o
  // disparo (POST) só ocorre no clique, então não há fetch automático a gatear.
  const canRefresh = useCapability('ai-summary-basic');

  const blocks = summary.blocks;
  const updatedAgo = formatRelativeTime(summary.generatedAt);
  const isBusy = summary.status === 'updating' || summary.regenerating;

  return (
    <div
      className="summary-panel"
      aria-label="Resumo vivo da conversa"
      data-testid="living-summary"
    >
      <div className="summary-panel__head">
        <SparklesIcon />
        <span>Resumo vivo</span>
        {summary.status === 'updating' && (
          <span
            className="inline-flex items-center gap-1 animate-pulse"
            data-testid="living-summary-updating"
            aria-live="polite"
            style={{ marginLeft: 4, fontSize: '10px', opacity: 0.85 }}
          >
            <span aria-hidden>···</span>
            <span>atualizando</span>
          </span>
        )}
        {updatedAgo !== null && (
          <span className="summary-panel__updated">atualizado {updatedAgo}</span>
        )}
        {canRefresh && (
          <button
            type="button"
            className="summary-panel__refresh"
            data-testid="living-summary-refresh"
            onClick={() => void summary.requestRegenerate()}
            disabled={isBusy}
            aria-label="Atualizar resumo"
            title="Atualizar resumo"
          >
            <RefreshIcon spinning={isBusy} />
            <span>Atualizar</span>
          </button>
        )}
      </div>

      {summary.regenerateError !== null && (
        <p
          className="summary-panel__refresh-error"
          role="alert"
          data-testid="living-summary-refresh-error"
        >
          {summary.regenerateError}
        </p>
      )}

      <div className="summary-panel__body">
        {blocks === null ? (
          <p
            style={{
              fontSize: 'var(--text-xs, 12px)',
              color: 'var(--ai-fg)',
              opacity: 0.7,
              fontStyle: 'italic',
            }}
          >
            {summary.status === 'loading'
              ? 'Carregando…'
              : 'Resumo vivo será gerado após algumas mensagens.'}
          </p>
        ) : (
          <>
            <LivingBlock label="Situação" value={blocks.contextOriginal} />
            <LivingBlock label="Cliente quer" value={blocks.necessidadeAtual} />
            <LivingBlock label="Atendente fez" value={blocks.evolucaoConversa} />
            <LivingListBlock label="Pendente do cliente" items={blocks.pendenciasAcordos} />
          </>
        )}
      </div>
    </div>
  );
}

function LivingBlock({
  label,
  value,
}: {
  label: string;
  value: string | null;
}): JSX.Element | null {
  if (value === null || value.trim() === '') return null;
  return (
    <div className="living-block">
      <div className="living-block__label">{label}</div>
      <div className="living-block__body">{value}</div>
    </div>
  );
}

function LivingListBlock({ label, items }: { label: string; items: string[] }): JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <div className="living-block">
      <div className="living-block__label">{label}</div>
      <div className="living-block__body">
        {items.length === 1 ? (
          items[0]
        ) : (
          <ul style={{ paddingLeft: 16, margin: 0, listStyle: 'disc' }}>
            {items.map((it, i) => (
              <li key={i}>{it}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function RefreshIcon({ spinning }: { spinning: boolean }): JSX.Element {
  return (
    <svg
      className={spinning ? 'icon animate-spin' : 'icon'}
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 3v6h-6" />
    </svg>
  );
}

function SparklesIcon(): JSX.Element {
  return (
    <svg
      className="icon"
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
      <path d="M19 14l.75 2.25L22 17l-2.25.75L19 20l-.75-2.25L16 17l2.25-.75L19 14z" />
    </svg>
  );
}

function formatRelativeTime(iso: string | null): string | null {
  if (iso === null) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return `há ${diffSec.toString()}s`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `há ${diffMin.toString()}min`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `há ${diffHr.toString()}h`;
  const diffDay = Math.floor(diffHr / 24);
  return `há ${diffDay.toString()}d`;
}
